import { useEffect, useState } from 'react';
import { useAuth } from '../providers/AuthProvider';
import { supabase } from '../integrations/supabase/client';

// ═══════════════════════════════════════════════════════════════
// TYPES (dados reais via RPCs; sem mocks)
// ═══════════════════════════════════════════════════════════════
export interface DashboardMetricsShape {
  totalClients?: number;
  activeClients?: number;
  mrr?: number;
  mrrGrowth?: number;
  totalMessages?: number;
  activeAlerts?: number;
  churnRate?: number;
  newClientsMonth?: number;
  revenueData?: Array<{ month: string; revenue?: number; clients?: number; date?: string; mrr?: number }>;
  planDistribution?: Array<{ name: string; value: number; color?: string }>;
}

export interface ClientListItem {
  id: string;
  name?: string;
  email?: string;
  plan?: string;
  status?: string;
  mrr?: number;
  createdAt?: string;
}

export interface RevenueMetricsShape {
  mrr: number;
  arr: number;
  churnRate: number;
  arpu: number;
}

export interface MasterDataContextType {
  isDemoMode: boolean;
  demoLabel?: string;
  isLoading: boolean;
  error: string | null;
  dashboardMetrics: DashboardMetricsShape | null;
  clients: ClientListItem[] | null;
  revenueMetrics: RevenueMetricsShape | null;
  refresh: () => Promise<void>;
}

// ═══════════════════════════════════════════════════════════════
// HOOK: useMasterData
// ═══════════════════════════════════════════════════════════════
export function useMasterData(): MasterDataContextType {
  const { user } = useAuth();
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dashboardMetrics, setDashboardMetrics] = useState<DashboardMetricsShape | null>(null);
  const [clients, setClients] = useState<ClientListItem[] | null>(null);
  const [revenueMetrics, setRevenueMetrics] = useState<RevenueMetricsShape | null>(null);

    useEffect(() => {
    if (user?.id) {
      refreshDashboard();
    } else {
      setIsDemoMode(false);
      setDashboardMetrics(null);
      setClients(null);
      setRevenueMetrics(null);
    }
 
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // ═════════════════════════════════════════════════════════════
  // FUNÇÕES: Fetch dados reais (quando não é demo)
  // ═════════════════════════════════════════════════════════════
  const refreshDashboard = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      // 1. Buscar métricas do dashboard
      const { data: metricsData, error: metricsError } = await supabase
        .rpc('get_master_dashboard_metrics');

      if (metricsError) throw metricsError;

      // 2. Buscar dados de receita
      const { data: revenueData, error: revenueError } = await supabase
        .rpc('get_master_revenue_data');

      if (revenueError) throw revenueError;

      // 3. Buscar distribuição de planos
      const { data: planData, error: planError } = await supabase
        .rpc('get_master_plan_distribution');

      if (planError) throw planError;

      // 4. Buscar lista de clientes
      const { data: clientsData, error: clientsError } = await supabase
        .rpc('get_master_clients_list');

      if (clientsError) throw clientsError;

      // Montar dashboardMetrics
      setDashboardMetrics({
        ...metricsData,
        revenueData: revenueData || [],
        planDistribution: planData || [],
      });

      // Montar clients
      setClients(clientsData || []);

      // Montar revenueMetrics
      setRevenueMetrics({
        mrr: metricsData?.mrr || 0,
        arr: (metricsData?.mrr || 0) * 12,
        churnRate: metricsData?.churnRate || 0,
        arpu: metricsData?.mrr / (metricsData?.activeClients || 1),
      });

      console.log('✅ Dados reais carregados do Master');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
      console.error('[useMasterData] Erro ao buscar dados:', err);
      setDashboardMetrics(null);
      setClients(null);
      setRevenueMetrics(null);
    } finally {
      setIsLoading(false);
    }
  };

  const refresh = async () => {
    await refreshDashboard();
  };

  return {
    isDemoMode,
    demoLabel: isDemoMode ? '📊 Dados de Demonstração' : undefined,
    isLoading,
    error,
    dashboardMetrics,
    clients,
    revenueMetrics,
    refresh,
  };
}
